import React, { lazy, Suspense, FC } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useParams } from 'react-router-dom';
import PostList from './components/PostList';
import TopHerdsPanel from './components/TopHerdsPanel';
import MobileHomePage from './components/Mobile/MobileHomePage';
import LoadingSpinner from './components/LoadingSpinner';

import { Container, Box } from '@mui/material/';

// Lazy-load non-initial routes to shrink initial bundle and speed boot
const PostCommentsPage = lazy(() => import('./components/PostCommentsPage'));
const UserPage = lazy(() => import('./components/UserPage'));
const AchievementsPage = lazy(() => import('./components/AchievementsPage'));
/** Permanent redirect from the old /r/ herd URLs. */
const LegacyHerdRedirect: FC<{ suffix?: string }> = ({ suffix = '' }) => {
  const { herd } = useParams();
  return <Navigate to={`/herd/${herd}${suffix}`} replace />;
};

const HerdPage = lazy(() => import('./components/HerdPage'));
const SearchResults = lazy(() => import('./components/SearchResults'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const ExplorePage = lazy(() => import('./components/Mobile/ExplorePage'));
const InboxPage = lazy(() => import('./components/Mobile/InboxPage'));
const MobileAchievementsLanding = lazy(() => import('./components/Mobile/MobileAchievementsLanding'));
const SettingsPage = lazy(() => import('./components/Mobile/SettingsPage'));
const PreferencesPage = lazy(() => import('./components/Mobile/PreferencesPage'));
const StorePage = lazy(() => import('./components/StorePage'));
const CustomizePage = lazy(() => import('./components/CustomizePage'));
const WalletPage = lazy(() => import('./components/WalletPage'));
const PoliciesPage = lazy(() => import('./components/PoliciesPage'));
const EarnPage = lazy(() => import('./components/EarnPage'));
const EarnTopicPage = lazy(() => import('./components/EarnTopicPage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const ChildSafetyPage = lazy(() => import('./components/ChildSafetyPage'));
const DeleteAccountPage = lazy(() => import('./components/DeleteAccountPage'));
const SubmitPage = lazy(() => import('./components/SubmitPage'));
const AdminFeedbackPage = lazy(() => import('./components/AdminFeedbackPage'));
const ModerationPage = lazy(() => import('./components/ModerationPage'));
const AdminReportsPage = lazy(() => import('./components/AdminReportsPage'));
const InfoPage = lazy(() => import('./components/InfoPage'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
    <LoadingSpinner text="" />
  </Box>
);

interface RoutesProps {
  isMobileApp?: boolean;
}

const Routes: FC<RoutesProps> = ({ isMobileApp = false }) => {
  const HomeLayout = () => (
    <Container
      disableGutters
      sx={{
        minWidth: '98%',
        mt: 'var(--space-stack-tight)',
        display: 'flex',
        // Align to top so the right panel can stay pinned via sticky while the
        // page scrolls behind the fixed glass bar.
        alignItems: 'flex-start',
        '@media (max-width:1280px)': {
          mt: 0,
          display: 'block',
        },
      }}
    >
      <Box sx={{ minWidth: '40vW', flexGrow: 1, minHeight: 0 }}>
        <PostList />
      </Box>
      <TopHerdsPanel />
    </Container>
  );

  return (
    <Suspense fallback={<RouteFallback />}>
      <RouterRoutes>
        {isMobileApp ? (
        // Mobile app routes - optimized layout for native apps
        <>
          <Route path="/" element={<MobileHomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/submit" element={<SubmitPage />} />
          <Route path="/submit/:id" element={<SubmitPage />} />
          <Route path="/messages" element={<InboxPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/preferences" element={<PreferencesPage />} />
          <Route path="/shop" element={<StorePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/customize" element={<CustomizePage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/wallet/ledger" element={<WalletPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          {/* Public, unauthenticated URLs submitted to the Play Console. */}
          <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/policies/child-safety" element={<ChildSafetyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          {/* Old bookmarks/deep links keep working. */}
          <Route path="/legal" element={<PoliciesPage />} />
          <Route path="/earn" element={<EarnPage />} />
          <Route path="/earn/:topic" element={<EarnTopicPage />} />
          <Route path="/referrals" element={<InfoPage page="referral" />} />
          <Route path="/creator-program" element={<InfoPage page="creator" />} />
          <Route path="/promote" element={<InfoPage page="promote" />} />
          <Route path="/help" element={<InfoPage page="help" />} />
          <Route path="/report-abuse" element={<InfoPage page="report-abuse" />} />
          <Route path="/achievements" element={<MobileAchievementsLanding />} />
          <Route path="/comments/:id" element={<PostCommentsPage />} />
          <Route path="/u/:username" element={<UserPage />} />
          <Route path="/u/:username/achievements" element={<AchievementsPage />} />
          <Route path="/herd/:herd" element={<HerdPage />} />
          <Route path="/herd/:herd/moderation" element={<ModerationPage />} />
          {/* Herds used to live at /r/. Redirect rather than 404 so anything
              bookmarked or already shared still resolves. */}
          <Route path="/r/:herd" element={<LegacyHerdRedirect />} />
          <Route path="/r/:herd/moderation" element={<LegacyHerdRedirect suffix="/moderation" />} />
          <Route path="/admin/reports" element={<AdminReportsPage />} />
          <Route path="/search/:query" element={<SearchResults />} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      ) : (
        // Web app routes - standard layout with sidebar
        <>
          <Route path="/" element={<HomeLayout />} />
          <Route path="/submit" element={<SubmitPage />} />
          <Route path="/submit/:id" element={<SubmitPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/messages" element={<InboxPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/preferences" element={<PreferencesPage />} />
          <Route path="/shop" element={<StorePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/customize" element={<CustomizePage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/wallet/ledger" element={<WalletPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          {/* Public, unauthenticated URLs submitted to the Play Console. */}
          <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/policies/child-safety" element={<ChildSafetyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          {/* Old bookmarks/deep links keep working. */}
          <Route path="/legal" element={<PoliciesPage />} />
          <Route path="/earn" element={<EarnPage />} />
          <Route path="/earn/:topic" element={<EarnTopicPage />} />
          <Route path="/referrals" element={<InfoPage page="referral" />} />
          <Route path="/creator-program" element={<InfoPage page="creator" />} />
          <Route path="/promote" element={<InfoPage page="promote" />} />
          <Route path="/help" element={<InfoPage page="help" />} />
          <Route path="/report-abuse" element={<InfoPage page="report-abuse" />} />
          {/* Admin triage is a desk activity — deliberately web-only and unlinked
              from any nav; the API 403s non-admins. */}
          <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
          <Route path="/comments/:id" element={<PostCommentsPage />} />
          <Route path="/u/:username" element={<UserPage />} />
          <Route path="/u/:username/achievements" element={<AchievementsPage />} />
          <Route path="/herd/:herd" element={<HerdPage />} />
          <Route path="/herd/:herd/moderation" element={<ModerationPage />} />
          {/* Herds used to live at /r/. Redirect rather than 404 so anything
              bookmarked or already shared still resolves. */}
          <Route path="/r/:herd" element={<LegacyHerdRedirect />} />
          <Route path="/r/:herd/moderation" element={<LegacyHerdRedirect suffix="/moderation" />} />
          <Route path="/admin/reports" element={<AdminReportsPage />} />
          <Route path="/search/:query" element={<SearchResults />} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      )}
      </RouterRoutes>
    </Suspense>
  );
};

export default Routes;
